import React, { useEffect, useState } from 'react';

export type ModuleStatus = 'active' | 'inactive';

interface ModuleEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (description: string, status: ModuleStatus) => void;
  module: {
    key: string;
    name: string;
    description: string;
    status: ModuleStatus;
    inUse: boolean;
  } | null;
}

export default function ModuleEditModal({
  isOpen,
  onClose,
  onSave,
  module,
}: ModuleEditModalProps) {
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<ModuleStatus>('active');

  const mKey = module?.key;
  const mDescription = module?.description;
  const mStatus = module?.status;

  useEffect(() => {
    if (!mKey) return;
    setDescription(mDescription || '');
    setStatus(mStatus ?? 'active');
  }, [mKey, mDescription, mStatus]);

  if (!isOpen || !module) return null;

  const toggleDisabled = module.inUse;

  const handleToggle = (checked: boolean) => {
    if (toggleDisabled) return;
    setStatus(checked ? 'active' : 'inactive');
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal" style={{ width: '480px' }}>
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: '1rem' }}>Edit Module — {module.name}</h2>
        </div>
        <div className="smrtdocs-modal-body">
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 6 }}>
              Description
            </label>
            <textarea
              className="manage-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: 6 }}>
              Status
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label className="switch" style={{ opacity: toggleDisabled ? 0.5 : 1 }}>
                <input
                  type="checkbox"
                  checked={status === 'active'}
                  disabled={toggleDisabled}
                  onChange={(e) => handleToggle(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
              <span
                style={{
                  background: status === 'active' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(229, 57, 53, 0.2)',
                  color: status === 'active' ? '#81c784' : '#ef9a9a',
                  padding: '3px 10px',
                  borderRadius: 10,
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '0.03em',
                }}
              >
                {status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </div>
            {toggleDisabled && (
              <p style={{ marginTop: 8, fontSize: '0.8rem', color: '#ef9a9a' }}>
                Module is in use and cannot be deactivated.
              </p>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button className="btn-cancel" onClick={onClose}>Cancel</button>
            <button
              className="btn-save"
              onClick={() => { onSave(description, status); onClose(); }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
